// FE-SCRM-055 — Settings → Tag library section.
//
// Lists every active AccountTag on this entity with its usage count
// across prospects / opportunities / contacts. Per-row actions:
//   • Rename — POST /api/v1/sales/tags/rename
//   • Merge into another tag — POST /api/v1/sales/tags/merge
//   • Delete — POST /api/v1/sales/tags/delete (archives + unlinks)
//
// ``RowActionsMenu`` provides the kebab dropdown so the action UX
// matches the rest of the round-2 surfaces. Inline modals capture
// the form input; on success the inventory query re-fetches and
// the row updates in place.

import { useState } from "react";

import {
  Button, Input, Modal,
  Table, THead, TBody, Tr, Th, Td,
} from "@/design-system";

import { RowActionsMenu } from "../components/RowActionsMenu";

import {
  useDeleteTagMutation,
  useMergeTagMutation,
  useRenameTagMutation,
  useTagInventoryQuery,
} from "./hooks";
import type { TagEntityType, TagInventoryRow } from "./types";


const TAB_LABELS: Record<TagEntityType, string> = {
  prospect: "Prospects",
  opportunity: "Opportunities",
  contact: "Contacts",
};


export function TagLibrarySection(): JSX.Element {
  const inventoryQ = useTagInventoryQuery();
  const renameM = useRenameTagMutation();
  const mergeM = useMergeTagMutation();
  const deleteM = useDeleteTagMutation();

  const [tab, setTab] = useState<TagEntityType>("prospect");
  const [renameTarget, setRenameTarget] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const rows: TagInventoryRow[] = inventoryQ.data?.[tab] ?? [];

  return (
    <section
      data-testid="settings-tag-library"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Tag library
        </h2>
        <p className="text-xs text-text-secondary">
          Every tag in use across Prospects, Opportunities, and
          Contacts. Rename to fix casing, merge to collapse
          duplicates, delete to retire a label.
        </p>
      </header>

      <nav
        data-testid="tag-library-tabs"
        className="flex gap-2 border-b border-border-subtle"
      >
        {(Object.keys(TAB_LABELS) as TagEntityType[]).map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`tag-library-tab-${t}`}
            onClick={() => setTab(t)}
            aria-selected={tab === t}
            className={[
              "px-3 py-1.5 text-xs font-medium",
              tab === t
                ? "border-b-2 border-teal text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </nav>

      {inventoryQ.isLoading ? (
        <p className="text-xs text-text-tertiary">Loading tags…</p>
      ) : null}

      {!inventoryQ.isLoading && rows.length === 0 ? (
        <p
          data-testid="tag-library-empty"
          className="text-xs text-text-tertiary"
        >
          No tags on {TAB_LABELS[tab].toLowerCase()} yet.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <Table
          layout="fixed"
          cellPadding="tight"
          className="border-collapse"
          data-testid="tag-library-table"
        >
          <THead>
            <Tr className="border-b border-border-subtle text-left text-[10px] uppercase tracking-wide text-text-tertiary">
              <Th>Tag</Th>
              <Th className="w-24 text-right">Usage</Th>
              <Th className="w-12" aria-hidden="true" />
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <Tr
                key={row.tag}
                data-testid={`tag-row-${row.tag}`}
                className="border-b border-border-subtle hover:bg-recessed"
              >
                <Td className="text-sm text-text-primary">
                  {row.tag}
                </Td>
                <Td className="text-right text-xs text-text-secondary">
                  {row.count}
                </Td>
                <Td className="text-right">
                  <RowActionsMenu
                    triggerLabel={`Actions for ${row.tag}`}
                    items={[
                      {
                        label: "Rename",
                        onClick: () => setRenameTarget(row.tag),
                      },
                      {
                        label: "Merge into…",
                        onClick: () => setMergeTarget(row.tag),
                      },
                      {
                        label: "Delete",
                        destructive: true,
                        onClick: () => setDeleteTarget(row.tag),
                      },
                    ]}
                  />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      ) : null}

      {renameTarget ? (
        <RenameTagModal
          fromTag={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSubmit={(toTag) =>
            renameM.mutate(
              { from_tag: renameTarget, to_tag: toTag },
              { onSuccess: () => setRenameTarget(null) },
            )}
          isPending={renameM.isPending}
          errorMessage={renameM.isError ? formatErr(renameM.error) : null}
        />
      ) : null}

      {mergeTarget ? (
        <MergeTagModal
          fromTag={mergeTarget}
          options={rows.filter((r) => r.tag !== mergeTarget)}
          onClose={() => setMergeTarget(null)}
          onSubmit={(intoTag) =>
            mergeM.mutate(
              { from_tag: mergeTarget, into_tag: intoTag },
              { onSuccess: () => setMergeTarget(null) },
            )}
          isPending={mergeM.isPending}
          errorMessage={mergeM.isError ? formatErr(mergeM.error) : null}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteTagModal
          tag={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() =>
            deleteM.mutate(
              { tag: deleteTarget },
              { onSuccess: () => setDeleteTarget(null) },
            )}
          isPending={deleteM.isPending}
          errorMessage={deleteM.isError ? formatErr(deleteM.error) : null}
        />
      ) : null}
    </section>
  );
}


function RenameTagModal({
  fromTag, onClose, onSubmit, isPending, errorMessage,
}: {
  fromTag: string;
  onClose: () => void;
  onSubmit: (toTag: string) => void;
  isPending: boolean;
  errorMessage: string | null;
}) {
  const [next, setNext] = useState(fromTag);
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename tag"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!next.trim() || next === fromTag || isPending}
            onClick={() => onSubmit(next.trim())}
            data-testid="rename-tag-submit"
          >
            {isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label="New name"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          data-testid="rename-tag-input"
        />
        {errorMessage ? (
          <p
            role="alert"
            data-testid="rename-tag-error"
            className="text-xs text-error"
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}


function MergeTagModal({
  fromTag, options, onClose, onSubmit, isPending, errorMessage,
}: {
  fromTag: string;
  options: TagInventoryRow[];
  onClose: () => void;
  onSubmit: (intoTag: string) => void;
  isPending: boolean;
  errorMessage: string | null;
}) {
  const [into, setInto] = useState<string>(options[0]?.tag ?? "");
  return (
    <Modal
      open
      onClose={onClose}
      title={`Merge "${fromTag}" into…`}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!into || into === fromTag || isPending}
            onClick={() => onSubmit(into)}
            data-testid="merge-tag-submit"
          >
            {isPending ? "Merging…" : "Merge"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-text-secondary">
          Every row currently tagged{" "}
          <span className="font-medium text-text-primary">
            {fromTag}
          </span>{" "}
          will be re-tagged with your selection. The source tag is
          archived after the merge.
        </p>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Merge into
          <select
            value={into}
            onChange={(e) => setInto(e.target.value)}
            data-testid="merge-tag-select"
            className="rounded border border-border-subtle bg-raised px-2 py-1.5 text-sm text-text-primary"
          >
            {options.length === 0 ? (
              <option value="">No other tags available</option>
            ) : null}
            {options.map((o) => (
              <option key={o.tag} value={o.tag}>
                {o.tag} ({o.count})
              </option>
            ))}
          </select>
        </label>
        {errorMessage ? (
          <p
            role="alert"
            data-testid="merge-tag-error"
            className="text-xs text-error"
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}


function DeleteTagModal({
  tag, onClose, onConfirm, isPending, errorMessage,
}: {
  tag: string;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  errorMessage: string | null;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Delete tag"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={isPending}
            onClick={onConfirm}
            data-testid="delete-tag-confirm"
          >
            {isPending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        <p className="text-xs text-text-secondary">
          Delete{" "}
          <span className="font-medium text-text-primary">{tag}</span>?
          The tag is archived and unlinked from every row that
          currently carries it. This can&rsquo;t be undone.
        </p>
        {errorMessage ? (
          <p
            role="alert"
            data-testid="delete-tag-error"
            className="text-xs text-error"
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}


function formatErr(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
